import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, Select, TextArea, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { Chip } from '@/components/Chip'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { toLocalInputValue } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'
import { uploadAttachment } from '@/lib/attachments'
import { PhotoAttachInput } from '@/components/PhotoAttachInput'
import { supabase } from '@/lib/supabase'
import { payloadValue, useEditableEvent } from '@/lib/editEvent'

type MeasurementType = 'weight' | 'height' | 'head_circumference' | 'temperature'

const TYPES: { key: MeasurementType; label: string; units: string[]; defaultUnit: string; range: [number, number] }[] = [
  { key: 'weight',              label: 'Weight',              units: ['kg', 'lb', 'g', 'oz'],   defaultUnit: 'kg', range: [0.2, 200] },
  { key: 'height',              label: 'Height / length',     units: ['cm', 'in'],              defaultUnit: 'cm', range: [10, 250] },
  { key: 'head_circumference',  label: 'Head circumference',  units: ['cm', 'in'],              defaultUnit: 'cm', range: [20, 70] },
  { key: 'temperature',         label: 'Temperature',         units: ['C', 'F'],                defaultUnit: 'C',  range: [30, 45] },
]

/** Normalize to canonical unit for charting later. Doesn't change the stored value/unit. */
export function normalizeForStorage(mt: MeasurementType, value: number, unit: string): { value: number; unit: string } {
  if (mt === 'weight' && unit === 'lb') return { value: +(value * 0.453592).toFixed(3), unit: 'kg' }
  if (mt === 'weight' && unit === 'oz') return { value: +(value * 0.0283495).toFixed(3), unit: 'kg' }
  if (mt === 'weight' && unit === 'g')  return { value: +(value / 1000).toFixed(3), unit: 'kg' }
  if (mt === 'height' && unit === 'in') return { value: +(value * 2.54).toFixed(2), unit: 'cm' }
  if (mt === 'head_circumference' && unit === 'in') return { value: +(value * 2.54).toFixed(2), unit: 'cm' }
  if (mt === 'temperature' && unit === 'F') return { value: +((value - 32) * 5 / 9).toFixed(2), unit: 'C' }
  return { value, unit }
}

export function GrowthLoggerScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const edit = useEditableEvent(['growth.recorded'])

  const [type, setType] = useState<MeasurementType>('weight')
  const [value, setValue] = useState('')
  const [unit, setUnit] = useState('kg')
  const [occurredAt, setOccurredAt] = useState<string>(toLocalInputValue(new Date()))
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [prefilled, setPrefilled] = useState(false)

  useEffect(() => {
    if (!edit.original || prefilled) return
    const p = edit.original.payload
    const mt = payloadValue<MeasurementType>(p, 'measurement_type')
    if (mt) setType(mt)
    const v = payloadValue<number>(p, 'value')
    if (v != null) setValue(String(v))
    const u = payloadValue<string>(p, 'unit')
    if (u) setUnit(u)
    const n = payloadValue<string>(p, 'note')
    if (n) setNote(n)
    setOccurredAt(toLocalInputValue(new Date(edit.original.occurred_at)))
    setPrefilled(true)
  }, [edit.original, prefilled])

  // Keep unit in the allowed list for the current type — pick default when type changes.
  useEffect(() => {
    if (prefilled) return
    const t = TYPES.find((x) => x.key === type)
    if (t && !t.units.includes(unit)) setUnit(t.defaultUnit)
  }, [type, unit, prefilled])

  if (!person || !familyId || !user) return <Screen title="Log growth" back />

  const typeMeta = TYPES.find((x) => x.key === type)!
  const packColor = PACK_COLORS.health.light

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      if (!person || !familyId || !user) return
      const num = parseFloat(value)
      if (!Number.isFinite(num) || num <= 0) throw new Error('Enter a value')
      const [lo, hi] = typeMeta.range
      if (num < lo || num > hi) throw new Error(`${typeMeta.label} should be between ${lo} and ${hi} ${unit}`)

      const { value: canonicalValue, unit: canonicalUnit } = normalizeForStorage(type, num, unit)

      const at = new Date(occurredAt)
      if (Number.isNaN(at.getTime())) throw new Error('Invalid date')

      // INSERT into measurements (queryable source of truth) + emit growth.recorded event
      // (for timeline + cross-device replication).
      if (edit.original) {
        // Corrections: don't dupe the measurements row — just emit the superseding event.
        // The timeline reads the events list (with supersedes filter), so the correction is visible.
        // (A future migration could add a projection that keeps `measurements` in sync with the
        //  latest non-superseded growth.recorded event; for Tier 1, the event is authoritative.)
      } else {
        const { error: measErr } = await supabase.from('measurements').insert({
          family_id: familyId,
          person_id: person.id,
          measurement_type: type,
          value: canonicalValue,
          unit: canonicalUnit,
          measured_at: at.toISOString(),
          measured_by: 'parent', // CHECK constraint requires role: 'parent'|'provider'|'self'|'manual'|'import'. User UUID is tracked via auth context / events.user_id.
          source: 'growth_logger',
        })
        if (measErr) throw new Error(`Failed to record measurement: ${measErr.message}`)
      }

      const eventId = await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType: 'growth.recorded',
        payload: {
          measurement_type: type,
          value: canonicalValue,
          unit: canonicalUnit,
          entered_value: num,
          entered_unit: unit,
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(photos.length > 0 ? { has_attachment: true } : {}),
        } as import('@/lib/types').JSONValue,
        occurredAt: at,
        supersedes: edit.supersedesId,
      })

      for (const file of photos) {
        await uploadAttachment({ familyId, userId: user.id, file, eventId })
      }

      if (edit.original) nav('/timeline'); else nav(-1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const isEditing = !!edit.original
  return (
    <Screen title={isEditing ? 'Correct measurement' : 'Log growth'} back>
      <form onSubmit={save} className="px-5 py-5 space-y-6">
        {edit.loading && <div className="text-caption text-text-tertiary">Loading original entry…</div>}
        {edit.error && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{edit.error}</div>
        )}
        {isEditing && (
          <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-3 text-caption text-text-secondary">
            Correcting an earlier measurement. Saving creates a new entry that supersedes the original.
          </div>
        )}

        <div>
          <Kicker>Measurement</Kicker>
          <div className="mt-3 flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <Chip key={t.key} color={packColor} emphasis={type === t.key ? 'filled' : 'quiet'} onClick={() => setType(t.key)}>
                {t.label}
              </Chip>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Value">
            <TextInput
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={type === 'weight' ? '3.4' : type === 'height' ? '50' : type === 'head_circumference' ? '35' : '37.0'}
              required
              autoFocus
            />
          </Field>
          <Field label="Unit">
            <Select value={unit} onChange={(e) => setUnit(e.target.value)}>
              {typeMeta.units.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="When">
          <TextInput type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        </Field>

        <Field label="Note (optional)" hint="Where was this measured? Pediatric visit, home scale, etc.">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        <PhotoAttachInput
          photos={photos}
          onChange={setPhotos}
          label="Photos (optional)"
          hint="Scale readout, measuring tape, sticker from pediatric visit."
          compact
        />

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{err}</div>
        )}

        <Button type="submit" variant="primary" size="lg" packColor={packColor} loading={busy} className="w-full">
          {isEditing ? 'Save correction' : 'Save measurement'}
        </Button>
      </form>
    </Screen>
  )
}
